<template>
  <div class="animated fadeIn">

    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="fa fa-server mr-2 text-primary"></i>Systems</h4>
        <small class="text-muted">Tipsoi server instances the Issue Tracker can log against</small>
      </b-col>
      <b-col class="text-right">
        <b-button variant="outline-secondary" size="sm" @click="load" :disabled="loading">
          <i class="icon-reload mr-1"></i>Refresh
        </b-button>
      </b-col>
    </b-row>

    <!-- Stat strip -->
    <b-row class="mb-3">
      <b-col sm="6" md="3" class="mb-2">
        <div class="stat-card">
          <div class="stat-num">{{ systems.length }}</div>
          <div class="stat-label"><i class="fa fa-server mr-1"></i>Total Systems</div>
        </div>
      </b-col>
      <b-col sm="6" md="3" class="mb-2">
        <div class="stat-card stat-hrm">
          <div class="stat-num">{{ countBy('HRM') }}</div>
          <div class="stat-label">HRM</div>
        </div>
      </b-col>
      <b-col sm="6" md="3" class="mb-2">
        <div class="stat-card stat-cs">
          <div class="stat-num">{{ countBy('CS') }}</div>
          <div class="stat-label">CS</div>
        </div>
      </b-col>
      <b-col sm="6" md="3" class="mb-2">
        <div class="stat-card stat-api">
          <div class="stat-num">{{ apiCount }}<span class="stat-of">/{{ systems.length }}</span></div>
          <div class="stat-label"><i class="fa fa-plug mr-1"></i>API Wired</div>
        </div>
      </b-col>
    </b-row>

    <b-alert show variant="light" class="border small text-muted mb-3">
      <i class="fa fa-info-circle mr-1 text-primary"></i>
      Systems are configured in <code>application.properties</code>
      (<code>issue.tracker.systems[n].*</code>). Add an <code>api-base-url</code> for a system to wire it up
      for live cross-server issue sync. Credentials are never stored here.
    </b-alert>

    <!-- Toolbar -->
    <b-row class="mb-3 align-items-center">
      <b-col md="6" class="mb-2 mb-md-0">
        <b-input-group size="sm">
          <b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
          <b-form-input v-model="search" type="search" placeholder="Search system name or code…" />
        </b-input-group>
      </b-col>
      <b-col md="6" class="text-md-right">
        <b-form-radio-group v-model="categoryFilter" size="sm" buttons button-variant="outline-secondary">
          <b-form-radio value="ALL">All</b-form-radio>
          <b-form-radio value="HRM">HRM</b-form-radio>
          <b-form-radio value="CS">CS</b-form-radio>
          <b-form-radio value="OTHER">Other</b-form-radio>
        </b-form-radio-group>
      </b-col>
    </b-row>

    <div v-if="loading" class="text-center py-5 text-muted">
      <b-spinner variant="primary" /> Loading systems…
    </div>

    <div v-else-if="filtered.length === 0" class="text-center py-5 text-muted">
      <i class="fa fa-server fa-2x mb-2 d-block"></i>
      No systems match. Configure them under <code>issue.tracker.systems</code>.
    </div>

    <!-- System cards -->
    <b-row v-else>
      <b-col sm="6" lg="4" xl="3" v-for="s in filtered" :key="s.code" class="mb-3">
        <div class="sys-card" :class="'cat-border-' + catClass(s.category)">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <b-badge :variant="catVariant(s.category)" class="text-uppercase">{{ s.category }}</b-badge>
            <b-badge v-if="s.local" variant="dark" title="This panel talks to this server directly">
              <i class="fa fa-home mr-1"></i>Local
            </b-badge>
          </div>
          <div class="sys-name">{{ s.name }}</div>
          <div class="sys-code">{{ s.code }}</div>

          <div class="sys-meta">
            <span v-if="s.projectId" class="mr-3"><i class="fa fa-folder-o mr-1"></i>Project {{ s.projectId }}</span>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
            <span v-if="s.apiConfigured" class="pill pill-on"><i class="fa fa-plug mr-1"></i>API wired</span>
            <span v-else class="pill pill-off"><i class="fa fa-plug mr-1"></i>Not wired</span>
            <b-badge v-if="!s.enabled" variant="secondary">disabled</b-badge>
          </div>
        </div>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import issueConfigService from '@/services/issueConfigService'

export default {
  name: 'IssueSystems',
  data: function() {
    return {
      loading: false,
      systems: [],
      search: '',
      categoryFilter: 'ALL'
    }
  },
  computed: {
    apiCount: function() {
      return this.systems.filter(function(s) { return s.apiConfigured }).length
    },
    filtered: function() {
      var q = this.search.trim().toLowerCase()
      var cat = this.categoryFilter
      return this.systems.filter(function(s) {
        var matchCat = cat === 'ALL'
          ? true
          : (cat === 'OTHER' ? (s.category !== 'HRM' && s.category !== 'CS') : s.category === cat)
        if (!matchCat) { return false }
        if (!q) { return true }
        return (s.name && s.name.toLowerCase().indexOf(q) !== -1) ||
               (s.code && s.code.toLowerCase().indexOf(q) !== -1)
      })
    }
  },
  created: function() { this.load() },
  methods: {
    load: function() {
      var self = this
      self.loading = true
      issueConfigService.getServers(
        function(data) { self.systems = data || []; self.loading = false },
        function(err) {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    countBy: function(cat) {
      return this.systems.filter(function(s) { return s.category === cat }).length
    },
    catVariant: function(cat) {
      if (cat === 'HRM') { return 'primary' }
      if (cat === 'CS') { return 'success' }
      if (cat === 'ADMIN') { return 'info' }
      return 'secondary'
    },
    catClass: function(cat) {
      if (cat === 'HRM') { return 'hrm' }
      if (cat === 'CS') { return 'cs' }
      if (cat === 'ADMIN') { return 'admin' }
      return 'other'
    }
  }
}
</script>

<style scoped>
.stat-card { background: #fff; border: 1px solid #e4e7ea; border-radius: 8px; padding: 14px 16px;
             box-shadow: 0 .125rem .25rem rgba(0,0,0,.075); }
.stat-num { font-size: 1.7rem; font-weight: 700; line-height: 1; color: #23282c; }
.stat-of  { font-size: 1rem; color: #a4b7c1; font-weight: 400; }
.stat-label { font-size: .72rem; text-transform: uppercase; letter-spacing: .04em; color: #8a93a2; margin-top: 6px; }
.stat-hrm { border-left: 4px solid #20a8d8; }
.stat-cs  { border-left: 4px solid #4dbd74; }
.stat-api { border-left: 4px solid #f86c6b; }

.sys-card { background: #fff; border: 1px solid #e4e7ea; border-left-width: 4px; border-radius: 8px;
            padding: 14px 16px; height: 100%; box-shadow: 0 1px 2px rgba(0,0,0,.05);
            transition: box-shadow .15s ease, transform .15s ease; }
.sys-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,.10); transform: translateY(-2px); }
.cat-border-hrm   { border-left-color: #20a8d8; }
.cat-border-cs    { border-left-color: #4dbd74; }
.cat-border-admin { border-left-color: #63c2de; }
.cat-border-other { border-left-color: #c8ced3; }
.sys-name { font-size: 1.05rem; font-weight: 700; color: #23282c; }
.sys-code { font-family: monospace; font-size: .75rem; color: #8a93a2; margin-bottom: 6px; }
.sys-meta { font-size: .8rem; color: #6a7887; min-height: 18px; }

.pill { font-size: .72rem; font-weight: 600; padding: 2px 8px; border-radius: 20px; }
.pill-on  { color: #2a7a48; background: #e6f6ec; }
.pill-off { color: #8a93a2; background: #f0f3f5; }
</style>
